import { z } from 'zod';
import axios from 'axios';
import { adapterClient, handleApiError } from './client';
import type { SessionView } from '@/types/session';
import { validateCredentials, validatePassword } from '@/lib/auth-validation';
import { useAuthStore } from '@/stores/authStore';

const sessionSchema = z.object({
  principalId: z.string().regex(/^0x[0-9a-f]{32}$/),
  accountId: z.string().regex(/^0x[0-9a-f]{64}$/),
  displayName: z.string(),
  csrfToken: z.string().min(1),
  isAdmin: z.boolean(),
});
export async function getSession(
  signal?: AbortSignal
): Promise<SessionView | null> {
  try {
    return sessionSchema.parse(
      (await adapterClient.get('/auth/session', { signal })).data
    );
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401)
      return null;
    handleApiError(error, 'session');
  }
}
export async function loginPassword(username: string, password: string) {
  validateCredentials(username, password);
  try {
    return sessionSchema.parse(
      (await adapterClient.post('/auth/login', { username, password })).data
    );
  } catch (error) {
    handleApiError(error, 'login');
  }
}
export async function registerAccount(
  username: string,
  password: string,
  displayName: string
) {
  validateCredentials(username, password, displayName);
  try {
    return sessionSchema.parse(
      (
        await adapterClient.post('/auth/register', {
          username,
          password,
          displayName,
        })
      ).data
    );
  } catch (error) {
    handleApiError(error, 'register');
  }
}
export async function logoutSession() {
  try {
    await adapterClient.post('/auth/logout');
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) return;
    handleApiError(error, 'logout');
  }
}
function securitySession() {
  const session = useAuthStore.getState().session;
  if (!session) throw new Error('Sign in to manage account security.');
  return session;
}
function checkSecuritySession(session: SessionView) {
  const current = useAuthStore.getState().session;
  if (
    current?.principalId !== session.principalId ||
    current.csrfToken !== session.csrfToken
  )
    throw new Error('Your session changed. Reload account security.');
}
const passkeyViewSchema = z.object({
  id: z.string().min(1),
  createdAt: z.number().int().nonnegative().safe().max(8_640_000_000_000),
});
export async function getPasskeys(signal?: AbortSignal) {
  const session = securitySession();
  try {
    const response = await adapterClient.get('/auth/passkeys', { signal });
    checkSecuritySession(session);
    return z.array(passkeyViewSchema).parse(response.data);
  } catch (error) {
    handleApiError(error, 'passkeys-list');
  }
}
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  signal?: AbortSignal
) {
  const session = securitySession();
  validatePassword(currentPassword);
  validatePassword(newPassword, true);
  try {
    const response = await adapterClient.post(
      '/auth/password',
      { currentPassword, newPassword },
      { signal }
    );
    checkSecuritySession(session);
    if (response.status !== 204)
      throw new Error(
        'Unexpected password-change confirmation. Check by signing in again.'
      );
  } catch (error) {
    handleApiError(error, 'password-change');
  }
}
export async function removePasskey(
  credentialId: string,
  currentPassword: string,
  signal?: AbortSignal
) {
  const session = securitySession();
  const id = passkeyViewSchema.shape.id.parse(credentialId);
  validatePassword(currentPassword);
  try {
    const response = await adapterClient.post(
      '/auth/passkeys/' + encodeURIComponent(id) + '/remove',
      { currentPassword },
      { signal }
    );
    checkSecuritySession(session);
    if (response.status !== 204)
      throw new Error(
        'Unexpected passkey-removal confirmation. Refresh the list.'
      );
  } catch (error) {
    handleApiError(error, 'passkey-remove');
  }
}
export function supportsPasskeys() {
  return (
    typeof PublicKeyCredential !== 'undefined' &&
    typeof PublicKeyCredential.parseCreationOptionsFromJSON === 'function' &&
    typeof PublicKeyCredential.parseRequestOptionsFromJSON === 'function' &&
    typeof PublicKeyCredential.prototype.toJSON === 'function' &&
    !!navigator.credentials &&
    window.isSecureContext
  );
}
function checkPasskeys() {
  if (!supportsPasskeys())
    throw new Error(
      'Passkeys are unavailable in this browser. Use password login or a current browser on HTTPS (localhost for development).'
    );
}
function passkeyError(error: unknown): never {
  if (error instanceof DOMException) {
    throw new Error(
      ['NotAllowedError', 'AbortError'].includes(error.name)
        ? 'Passkey authentication was cancelled or timed out. You can retry or use your password.'
        : 'The passkey could not be used. Check the browser and configured origin, or use your password.'
    );
  }
  handleApiError(error, 'passkey');
}
export async function loginPasskey(username: string): Promise<SessionView> {
  checkPasskeys();
  if (!/^[A-Za-z0-9._-]{3,64}$/.test(username))
    throw new Error(
      'Enter a valid username (3–64 letters, numbers, dots, underscores or hyphens).'
    );
  try {
    const { data } = await adapterClient.post<{
      ceremonyId: string;
      options: { publicKey: PublicKeyCredentialRequestOptionsJSON };
    }>('/auth/passkeys/login/options', { username });
    const credential = (await navigator.credentials.get({
      publicKey: PublicKeyCredential.parseRequestOptionsFromJSON(
        data.options.publicKey
      ),
    })) as PublicKeyCredential | null;
    if (!credential)
      throw new Error(
        'No passkey was returned. Try again or use your password.'
      );
    const result = await adapterClient.post('/auth/passkeys/login/verify', {
      ceremonyId: data.ceremonyId,
      credential: credential.toJSON(),
    });
    return sessionSchema.parse(result.data);
  } catch (error) {
    passkeyError(error);
  }
}
export async function enrollPasskey(
  currentPassword: string,
  signal?: AbortSignal
) {
  checkPasskeys();
  const session = securitySession();
  validatePassword(currentPassword);
  try {
    const { data } = await adapterClient.post<{
      ceremonyId: string;
      options: { publicKey: PublicKeyCredentialCreationOptionsJSON };
    }>('/auth/passkeys/register/options', { currentPassword }, { signal });
    checkSecuritySession(session);
    signal?.throwIfAborted();
    const credential = (await navigator.credentials.create({
      publicKey: PublicKeyCredential.parseCreationOptionsFromJSON(
        data.options.publicKey
      ),
      signal,
    })) as PublicKeyCredential | null;
    if (!credential)
      throw new Error(
        'No passkey was created. Your password account is still usable.'
      );
    checkSecuritySession(session);
    signal?.throwIfAborted();
    const response = await adapterClient.post(
      '/auth/passkeys/register/verify',
      {
        ceremonyId: data.ceremonyId,
        credential: credential.toJSON(),
      },
      { signal }
    );
    checkSecuritySession(session);
    if (response.status !== 201)
      throw new Error(
        'Unexpected passkey-registration confirmation. Refresh the list.'
      );
  } catch (error) {
    passkeyError(error);
  }
}
